import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Chip, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import DashboardToolbar from '../components/dashboard/DashboardToolbar';
import DashboardKpiCard from '../components/dashboard/DashboardKpiCard';
import ModuleSlicer from '../components/dashboard/ModuleSlicer';
import { useMaintenanceData, useWarrantyData } from '../hooks/useDashboardData';
import { useOperationsData } from '../hooks/useOperationsData';
import {
  getPeriodLabel, getDepartmentsFromMaintenance,
  getDefaultDashboardPeriod, isNoSelection,
  getDashboardDefaultFilters,
  filterMaintenanceByDept, getBreakdownList, sumByProduct,
  calcRejectionRate, isOpenRecord,
  collectPeriodKeys, getFiscalYearsFromData, filterPeriodsByYear,
  filterMaintenanceDashboard, filterYearMonthDashboard,
} from '../utils/dashboardUtils';
import { exportMain } from '../utils/dashboardExport';
import { RejectionByProductPeriodChart, MachineMonthlyClusterChart, ProductSupplyBarChart } from '../components/dashboard/DashboardCharts';
import moment from 'moment';

const ModulePanel = ({ title, children, colors, span, height = '320px', slicer, gridRow }) => (
  <Box
    gridColumn={`span ${span}`}
    gridRow={gridRow}
    bgcolor={colors.primary[400]}
    p="12px"
    display="flex"
    flexDirection="column"
    minHeight={height}
    height={height}
  >
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.5}>
      <Typography color={colors.greenAccent[500]} variant="subtitle1" fontWeight={700}>{title}</Typography>
      {slicer}
    </Box>
    <Box flex={1} minHeight={0} height={height} overflow="hidden">{children}</Box>
  </Box>
);

const getOrderStatusChipSx = (status, colors) => {
  const key = String(status || 'NEW').toUpperCase();
  if (key === 'ORDERED' || key === 'PENDING') {
    return { bgcolor: 'rgba(255, 152, 0, 0.18)', color: '#ffb74d', fontWeight: 600, fontSize: 11, height: 22 };
  }
  if (key === 'NEW') {
    return { bgcolor: 'rgba(226, 114, 110, 0.2)', color: '#e2726e', fontWeight: 600, fontSize: 11, height: 22 };
  }
  return { bgcolor: 'rgba(76, 206, 172, 0.18)', color: colors.greenAccent[400], fontWeight: 600, fontSize: 11, height: 22 };
};

const STORE_COLUMNS = 'minmax(160px, 2.5fr) minmax(110px, 1.3fr) 56px 72px 88px';

const DASHBOARD_ROW_HEIGHT = '460px';

const prunePeriods = (selected, available) => {
  if (isNoSelection(selected)) return selected;
  if (!selected.length) return [];
  const valid = selected.filter((p) => available.includes(p));
  if (valid.length) return valid;
  const def = getDefaultDashboardPeriod();
  if (available.includes(def)) return [def];
  return available.length ? [available[available.length - 1]] : [];
};

const MainDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { records: maintenance, loading: l1, error: e1, refresh: r1 } = useMaintenanceData();
  const { supplyRows, warrantyRows, rejectionRows, loading: l2, error: e2, refresh: r2 } = useWarrantyData();
  const { inventoryItems, loading: opsLoading, refresh: r3 } = useOperationsData();
  const [years, setYears] = useState(() => getDashboardDefaultFilters('business').years);
  const [periods, setPeriods] = useState(() => getDashboardDefaultFilters('business').periods);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const loading = l1 || l2;
  const error = e1 || e2;

  const allPeriods = useMemo(
    () => collectPeriodKeys(maintenance, supplyRows, warrantyRows, rejectionRows),
    [maintenance, supplyRows, warrantyRows, rejectionRows],
  );
  const periodKeys = useMemo(
    () => filterPeriodsByYear(allPeriods, years, 'fiscal'),
    [allPeriods, years],
  );
  const periodOptions = useMemo(
    () => periodKeys.map((p) => ({ value: p, label: p })),
    [periodKeys],
  );
  const yearOptions = useMemo(
    () => getFiscalYearsFromData(maintenance, supplyRows, warrantyRows, rejectionRows).map((y) => ({ value: y, label: y })),
    [maintenance, supplyRows, warrantyRows, rejectionRows],
  );

  const handleYearsChange = useCallback((newYears) => {
    setYears(newYears);
    const next = filterPeriodsByYear(allPeriods, newYears, 'fiscal');
    setPeriods((prev) => prunePeriods(prev, next));
  }, [allPeriods]);

  useEffect(() => {
    setPeriods((prev) => {
      if (isNoSelection(prev)) return prev;
      if (!prev.length) return prev;
      const valid = prev.filter((p) => periodKeys.includes(p));
      if (valid.length) return valid.length === prev.length ? prev : valid;
      return prunePeriods(prev, periodKeys);
    });
  }, [periodKeys]);

  const departmentList = useMemo(() => getDepartmentsFromMaintenance(maintenance), [maintenance]);
  const filteredMaint = useMemo(
    () => filterMaintenanceByDept(filterMaintenanceDashboard(maintenance, years, periods, 'fiscal'), departments),
    [maintenance, years, periods, departments],
  );
  const filteredRejection = useMemo(() => filterYearMonthDashboard(rejectionRows, years, periods, 'fiscal'), [rejectionRows, years, periods]);
  const filteredSupply = useMemo(() => filterYearMonthDashboard(supplyRows, years, periods, 'fiscal'), [supplyRows, years, periods]);
  const filteredWarranty = useMemo(() => filterYearMonthDashboard(warrantyRows, years, periods, 'fiscal'), [warrantyRows, years, periods]);
  const periodLabel = getPeriodLabel(years, periods);
  const breakdown = useMemo(() => getBreakdownList(filteredMaint, departments), [filteredMaint, departments]);

  const yearMaint = useMemo(
    () => filterMaintenanceByDept(filterMaintenanceDashboard(maintenance, years, [], 'fiscal'), departments),
    [maintenance, years, departments],
  );
  const yearSupply = useMemo(() => filterYearMonthDashboard(supplyRows, years, [], 'fiscal'), [supplyRows, years]);
  const yearWarranty = useMemo(() => filterYearMonthDashboard(warrantyRows, years, [], 'fiscal'), [warrantyRows, years]);

  const yearSupplyTotal = sumByProduct(yearSupply, products);
  const yearWarrantyTotal = sumByProduct(yearWarranty, products);
  const yearRejRate = calcRejectionRate(yearSupplyTotal, yearWarrantyTotal);
  const yearOpenMaint = yearMaint.filter(isOpenRecord).length;

  const kpis = useMemo(() => [
    { label: 'Supply Quantity (Year)', value: yearSupplyTotal },
    { label: 'Warranty Quantity (Year)', value: yearWarrantyTotal },
    { label: 'Rejection Rate (Year)', value: yearRejRate },
    { label: 'Maintenance Tasks (Year)', value: yearMaint.length },
    { label: 'Open Maintenance (Year)', value: yearOpenMaint },
    { label: 'Store Inventory Alerts', value: inventoryItems.length },
  ], [yearSupplyTotal, yearWarrantyTotal, yearRejRate, yearMaint.length, yearOpenMaint, inventoryItems.length]);

  const handleDownload = async () => {
    setDownloading(true);
    try { await exportMain(filteredMaint, filteredSupply, filteredWarranty, periodLabel, kpis); }
    finally { setDownloading(false); }
  };

  const handleRefresh = useCallback(() => {
    const defaults = getDashboardDefaultFilters('business');
    setYears(defaults.years);
    setPeriods(defaults.periods);
    setProducts(defaults.products);
    setDepartments(defaults.departments);
    r1();
    r2();
    r3();
  }, [r1, r2, r3]);

  if (error) {
    return (
      <Box ml="200px" mt="70px" p={2} width="calc(100% - 220px)">
        <Typography color="error">Failed to load dashboard data: {error}</Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Ensure MIS backend is running at {process.env.REACT_APP_MISBACKEND_URL}
        </Typography>
      </Box>
    );
  }

  return (
    <Box ml="200px" mt="70px" pl={2} width="calc(100% - 220px)">
      <DashboardToolbar
        variant="business"
        title="Business Operations Dashboard"
        subtitle="Warranty, maintenance, store, and production overview"
        years={years} periods={periods} yearOptions={yearOptions} periodOptions={periodOptions}
        onYearsChange={handleYearsChange} onPeriodsChange={setPeriods}
        periodLabel={periodLabel}
        onDownload={handleDownload} onRefresh={handleRefresh}
        downloading={downloading} loading={loading}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gridAutoRows="minmax(110px, auto)" gap="5px">
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Supply Quantity (Year)" value={yearSupplyTotal} format="number" />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Warranty Quantity (Year)" value={yearWarrantyTotal} format="number" />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Rejection Rate (Year)" value={yearRejRate} format="percent" isPercent />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Maintenance Tasks (Year)" value={yearMaint.length} format="number" />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Open Maintenance (Year)" value={yearOpenMaint} format="number" />
          </Box>
          <Box gridColumn="span 2">
            <DashboardKpiCard label="Store Inventory Alerts" value={inventoryItems.length} format="number" />
          </Box>

          <ModulePanel
            title="Warranty"
            colors={colors}
            span={5}
            height={DASHBOARD_ROW_HEIGHT}
            gridRow="span 1"
            slicer={<ModuleSlicer type="product" value={products} onChange={setProducts} colors={colors} />}
          >
            <Box display="flex" flexDirection="column" height="100%" gap={1}>
              <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                <Typography color={colors.grey[300]} variant="caption" mb={0.5} display="block" flexShrink={0}>
                  Total Supply By Product
                </Typography>
                <Box sx={{ position: 'relative', flex: 1, minHeight: 180, width: '100%' }}>
                  <ProductSupplyBarChart records={filteredSupply} product={products} />
                </Box>
              </Box>
              <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                <Typography color={colors.grey[300]} variant="caption" mb={0.5} display="block" flexShrink={0}>
                  Rejection Rate by Product Over Period
                </Typography>
                <Box sx={{ position: 'relative', flex: 1, minHeight: 180, width: '100%' }}>
                  <RejectionByProductPeriodChart records={filteredRejection} product={products} />
                </Box>
              </Box>
            </Box>
          </ModulePanel>

          <ModulePanel
            title="Maintenance"
            colors={colors}
            span={7}
            height={DASHBOARD_ROW_HEIGHT}
            gridRow="span 1"
            slicer={<ModuleSlicer type="department" value={departments} onChange={setDepartments} departments={departmentList} colors={colors} />}
          >
            <Box display="flex" flexDirection="column" height="100%" gap={0.5}>
              <Typography color={colors.grey[300]} variant="caption" mb={0.25} display="block" flexShrink={0}>
                Breakdown List
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1.4fr" gap={1} flex={1} minHeight={0}>
                <Box overflow="auto" pr={1}>
                {breakdown.length ? breakdown.map((row, i) => (
                  <Box key={i} display="grid" gridTemplateColumns="1.2fr 0.8fr 0.8fr 0.5fr" gap={0.5}
                    py={0.5} borderBottom={`1px solid ${colors.primary[500]}`}>
                    <Typography variant="caption" color={colors.grey[200]} noWrap>{row.section}</Typography>
                    <Typography variant="caption" color={colors.grey[200]} noWrap>{row.machine}</Typography>
                    <Typography variant="caption" color={colors.grey[300]}>
                      {row.startDate ? moment(row.startDate).format('M/D/YYYY') : '—'}
                    </Typography>
                    <Typography variant="caption" color={colors.grey[100]} fontWeight={600}>{row.ageing}d</Typography>
                  </Box>
                )) : (
                  <Typography variant="caption" color={colors.grey[400]}>No open breakdowns</Typography>
                )}
              </Box>
              <Box display="flex" flexDirection="column" minHeight={0}>
                <Typography color={colors.grey[300]} variant="caption" mb={0.5} display="block" flexShrink={0}>
                  Monthly Total Task Distribution per Machine
                </Typography>
                <Box sx={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
                  <MachineMonthlyClusterChart records={filteredMaint} />
                </Box>
              </Box>
            </Box>
            </Box>
          </ModulePanel>

          <ModulePanel title="Store" colors={colors} span={9} height="320px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexShrink={0}>
              <Typography color={colors.grey[300]} variant="caption">
                Inventory items at or below minimum stock level
              </Typography>
              {!opsLoading && inventoryItems.length > 0 && (
                <Chip
                  size="small"
                  label={`${inventoryItems.length} alert${inventoryItems.length === 1 ? '' : 's'}`}
                  sx={{ bgcolor: 'rgba(226, 114, 110, 0.2)', color: '#e2726e', fontWeight: 600, height: 22, fontSize: 11 }}
                />
              )}
            </Box>
            <Box overflow="auto" height="calc(100% - 32px)" sx={{ borderRadius: 1, border: `1px solid ${colors.primary[500]}` }}>
              {opsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={24} />
                </Box>
              ) : inventoryItems.length ? (
                <>
                  <Box
                    display="grid"
                    gridTemplateColumns={STORE_COLUMNS}
                    gap={0.75}
                    px={1}
                    py={0.75}
                    borderBottom={`1px solid ${colors.primary[500]}`}
                    sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: colors.primary[400] }}
                  >
                    {['Item', 'Category', 'Qty', 'Shortfall', 'Status'].map((header) => (
                      <Typography key={header} variant="caption" fontWeight={700} color={colors.grey[400]}>
                        {header}
                      </Typography>
                    ))}
                  </Box>
                  {inventoryItems.map((item, i) => (
                    <Box
                      key={item.id || item.name}
                      display="grid"
                      gridTemplateColumns={STORE_COLUMNS}
                      gap={0.75}
                      px={1}
                      py={0.85}
                      alignItems="center"
                      borderBottom={`1px solid ${colors.primary[500]}`}
                      bgcolor={i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.06)'}
                    >
                      <Typography variant="body2" color={colors.grey[100]} fontWeight={500} noWrap title={item.name}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color={colors.grey[400]} noWrap title={item.category || '—'}>
                        {item.category || '—'}
                      </Typography>
                      <Typography variant="caption" color={colors.grey[200]} fontWeight={600}>
                        {item.quantity}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color={item.shortfall > 0 ? '#e2726e' : colors.grey[500]}
                      >
                        {item.shortfall > 0 ? item.shortfall : 0}
                      </Typography>
                      <Chip
                        size="small"
                        label={item.orderStatus || 'NEW'}
                        sx={getOrderStatusChipSx(item.orderStatus, colors)}
                      />
                    </Box>
                  ))}
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" py={4} px={2}>
                  <Typography variant="body2" color={colors.grey[300]} fontWeight={500}>
                    No inventory alerts
                  </Typography>
                  <Typography variant="caption" color={colors.grey[500]} mt={0.5} textAlign="center">
                    All stocked items are at or above minimum levels
                  </Typography>
                </Box>
              )}
            </Box>
          </ModulePanel>

          <ModulePanel title="Production" colors={colors} span={3} height="320px">
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography variant="h5" color={colors.grey[300]} fontWeight={300}>
                Coming Soon
              </Typography>
            </Box>
          </ModulePanel>
        </Box>
      )}
    </Box>
  );
};

export default MainDashboard;
